import { Button, Drawer, Form, Space } from 'antd';
import { useDrawerStore } from '../../../../store';
import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { BOMStatusEnum } from '../../../../lib/enums';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../lib/utils';
import { BaseModel, FormField } from '../../../../types';
import { BillOfMaterialFilter } from '../../../../types/BillOfMaterial';
import SearchFilter from '../../../../components/ui/filters/SearchFilter';
import { FilterOutlined } from '@ant-design/icons';
import SubmitButton from '../../../../components/ui/SubmitButton';
import CustomForm from '../../../../components/ui/CustomForm';
import { useBillOfMaterialDetailStore } from '../../../../store/BillOfMaterialStore';
import { useGenerateMR } from '../../../../hooks/useBillOfMaterialHooks';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';

const BillOfMaterialDetailHeaderFilter = () => {
  const [form] = Form.useForm();
  const drawerStore = useDrawerStore();
  const { data, resetSelectedRowKeys } = useBillOfMaterialDetailStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState('');
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedValue] = useState<string | undefined>(
    searchParams.get('status') || BOMStatusEnum.ALL,
  );
  const startDate =
    searchParams.get('start') === undefined ||
    searchParams.get('start') === null
      ? dayjs().subtract(6, 'month').format(dateFormat)
      : searchParams.get('start');

  const endDate =
    searchParams.get('end') === undefined || searchParams.get('end') === null
      ? dayjs().add(2, 'month').format(dateFormat)
      : searchParams.get('end');

  const proder =
    searchParams.get('proder') === undefined ||
    searchParams.get('proder') === null
      ? ''
      : searchParams.get('proder');
  const sku =
    searchParams.get('sku') === undefined || searchParams.get('sku') === null
      ? ''
      : searchParams.get('sku');

  const statusOption: BaseModel[] = Object.keys(BOMStatusEnum).map(
    (key) =>
      ({
        id: key,
        code: BOMStatusEnum[key as keyof typeof BOMStatusEnum],
        description: BOMStatusEnum[key as keyof typeof BOMStatusEnum],
      }) as BaseModel,
  );
  const [filter, setFilter] = useState<BillOfMaterialFilter>({
    from: startDate,
    to: endDate,
    datepicker: [startDate, endDate],
    proder: proder,
    sku: sku,
    status: selectedValue,
  });

  const hasSelected = data.length > 0;
  const formFields: FormField[] = [
    {
      name: 'datepicker',
      label: 'Date coverage',
      rules: [{ required: true, message: 'Please select date coverage' }],
      type: 'date-range-picker',
      allowClear: false,
    },
    {
      name: 'sku',
      label: 'SKU',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'proder',
      label: 'Proder',
      type: 'input',
      allowClear: true,
    },
    {
      name: 'status',
      label: 'Status',
      isMultiple: false,
      type: 'select',
      values: statusOption,
      defaultValue: selectedValue,
    },
  ];

  const onFinish = (values: {
    datepicker: string;
    proder: string;
    status: string;
    sku: string;
  }) => {
    setSearchParams((prev) => {
      prev.set('from', dayjs(values.datepicker[0]).format(dateFormat));
      prev.set('to', dayjs(values.datepicker[1]).format(dateFormat));
      prev.set('proder', values.proder === undefined ? '' : values.proder);
      prev.set('sku', values.sku === undefined ? '' : values.sku);
      prev.set('status', values.status === undefined ? '' : values.status);
      return prev;
    });
    setFilter({
      from: dayjs(values.datepicker[0]).format(dateFormat),
      to: dayjs(values.datepicker[1]).format(dateFormat),
      datepicker: [
        dayjs(values.datepicker[0]).format(dateFormat),
        dayjs(values.datepicker[1]).format(dateFormat),
      ],
      proder: values.proder,
      sku: values.sku,
      status: values.status,
    });
    drawerStore.onClose();
  };

  const {
    mutate,
    isPending,
    isError: isUpdateError,
    error: updateError,
    isSuccess,
  } = useGenerateMR();

  const onClick = () => {
    mutate(data);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    resetSelectedRowKeys();
  };

  useEffect(() => {
    if (isSuccess) setIsInfoModalOpen(true);
    if (isUpdateError) {
      setIsErrorModalOpen(true);
      setErrorMess(updateError.message);
    }
  }, [isSuccess, isUpdateError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  return (
    <>
      <div className={'flex justify-between p-4'}>
        <Space direction="horizontal" size={12}>
          <SearchFilter />
        </Space>

        <div className={'flex space-x-2'}>
          <Button
            type="primary"
            onClick={onClick}
            disabled={!hasSelected}
            loading={isPending}
            style={{
              backgroundColor: !hasSelected ? 'bg-gray-300' : '#A23231',
              borderRadius: '0.75rem',
              color: !hasSelected ? 'text-gray-500' : 'white',
              fontWeight: 'bold',
            }}
          >
            Generate MR
            <span style={{ marginLeft: 8 }}>
              {hasSelected ? `(${data.length})` : ''}
            </span>
          </Button>
          <Button onClick={drawerStore.onClose} icon={<FilterOutlined />} />
        </div>
      </div>

      <Drawer
        onClose={drawerStore.onClose}
        open={drawerStore.isOpen}
        extra={
          <Space>
            <Button onClick={drawerStore.onClose}>Cancel</Button>
            <SubmitButton form={form}>Filter</SubmitButton>
          </Space>
        }
      >
        <CustomForm
          rowCol={1}
          data={filter}
          form={form}
          formFields={formFields}
          onFinish={onFinish}
          layout={'vertical'}
          formName={'form_drawer'}
        />
      </Drawer>
      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={`Purchase Order successfully generated`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default BillOfMaterialDetailHeaderFilter;
